import { useWorkflowCardStyles } from '../../../../ui/components/pure/workflow-card/workflow-card.styles';
import { Card, Grid } from '@mui/material';
import { Link } from 'react-router-dom';
import { ProjectAddress } from '@groundfloor-platform/mgmt-service-g-sdk';
import { decodeImageURL } from '../../../../ui/util/image-url';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { OptionalLink } from '../../../../ui/components/pure/optional-link/optional-link.component';

// Module-level so the reference stays stable across renders.
const INTERNAL_GROUNDFLOOR = ['internal-groundfloor'];

/**
 * IDs and types are carried even where nothing links on them yet, so a link can be added without
 * reshaping the card.
 */
export interface DeliveriesCardProps {
  recipient?: {
    id: string;
    type: string;
    name: string;
    link?: string;
    /** The occupant's or customer's unit, printed ahead of the project's address. */
    unit?: { id: string; name: string };
  };
  /** The project holding the lockers. */
  project: { id: string; name: string; projectType?: string; address?: ProjectAddress };
  /** The party the delivery came from — a carrier, project, customer or occupant. */
  deliveredBy?: {
    id: string;
    type: string;
    name: string;
    imageURL?: string;
    /** Linked for internal users only. */
    link?: string;
  };
  /** The courier who made the delivery, when one did. Shown to internal users only. */
  courier?: { id: string; name: string; link: string };
  barcode?: string;
  carrierReference?: string;
  recipientLabel?: string;
  deliveredByLabel?: string;
}

export function DeliveriesCard({
  recipient,
  project,
  deliveredBy,
  courier,
  barcode,
  carrierReference,
  recipientLabel = 'RECIPIENT',
  deliveredByLabel = 'DELIVERED BY'
}: DeliveriesCardProps) {
  const classes = useWorkflowCardStyles();
  return (
    <Card className={classes.card} elevation={0}>
      <Grid container>
        <Grid size={12}>
          <p className={classes.label}>{recipientLabel}</p>
          <div className={classes.recipientName}>
            {recipient?.link ? (
              <Link className={classes.recipientLink} to={recipient.link}>
                {recipient.name}
              </Link>
            ) : (
              recipient?.name
            )}
          </div>
          <div className={classes.cardTextGrey}>{project.name}</div>
          {recipient?.unit ? (
            <div>
              <AddressContent address={project.address} unit={recipient.unit.name} />
            </div>
          ) : null}
          {courier ? (
            <PermissionBoundary availableUiFeatures={INTERNAL_GROUNDFLOOR}>
              <p style={{ marginTop: '12px' }} className={classes.label}>
                COURIER
              </p>
              <div className={classes.recipientName}>
                <Link className={classes.recipientLink} to={courier.link}>
                  {courier.name}
                </Link>
              </div>
            </PermissionBoundary>
          ) : null}
          <p style={{ marginTop: '12px' }} className={classes.label}>
            {deliveredByLabel}
          </p>
          {deliveredBy?.imageURL ? (
            <p>
              {' '}
              <OptionalLink link={deliveredBy.link} availableUiFeatures={INTERNAL_GROUNDFLOOR}>
                <img
                  className={classes.deliveredByName}
                  style={{ height: '3em', width: 'auto' }}
                  src={decodeImageURL(deliveredBy.imageURL)}
                  alt={deliveredBy.name}
                />
              </OptionalLink>
            </p>
          ) : (
            <p className={classes.deliveredByName}>
              <OptionalLink link={deliveredBy?.link} availableUiFeatures={INTERNAL_GROUNDFLOOR}>
                {deliveredBy?.name || ''}
              </OptionalLink>
            </p>
          )}
          {barcode ? (
            <>
              <p className={classes.label}>BARCODE</p>
              <p className={classes.cardText}>{barcode}</p>
            </>
          ) : null}
          {carrierReference ? (
            <>
              <p className={classes.label}>CARRIER REFERENCE</p>
              <p className={classes.cardText}>{carrierReference}</p>
            </>
          ) : null}
        </Grid>
      </Grid>
    </Card>
  );
}

/** The unit, then the project's address. A project with no address still shows the unit. */
function AddressContent({ address, unit }: { address?: ProjectAddress; unit: string }) {
  const classes = useWorkflowCardStyles();

  return (
    <span className={classes.cardText}>
      {unit}
      {address ? (
        <>
          <span>{address.line1 ? ', ' : null}</span>
          {address.line1}
          <br />
          {address.line2 ? (
            <>
              {address.line2}
              <br />
            </>
          ) : null}
          {address.suburb} {address.state} {address.postcode}
        </>
      ) : null}
    </span>
  );
}
